'use client'
import { yupResolver } from '@hookform/resolvers/yup'
import { CheckCircle, PermMedia } from '@mui/icons-material'
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox'
import { Grid } from '@mui/material'
import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import axios from 'axios'
import { addDoc, collection, doc, getDoc } from 'firebase/firestore'
import { get } from 'lodash'
import moment from 'moment'
import Image from 'next/image'
import { posthog } from 'posthog-js'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import BlogCardsSection from 'src/components/Blogs/BlogCardsSection'
import CustomTextField from 'src/components/mui/CustomInput'
import { BlogType, getAllGhostPosts } from 'src/utils/GhostAPI'
import { db } from 'src/utils/firebase'
import { getCountry, getLocation } from 'src/utils/location'
import * as yup from 'yup'

interface FormValues {
  email: string
}

const validationSchema = yup.object({
  email: yup.string().email('Email is invalid').required('Email is required')
})

const CustomTabPanel = (props: any) => {
  const { children, value, index } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}>
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

export default function Blogs() {
  const [blogs, setBlogs] = useState<BlogType[]>([])
  const [blogsLoading, setBlogsLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState(false)
  const [tabValue, setTabValue] = useState(0)
  const [emailSent, setEmailSent] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty }
  } = useForm<FormValues>({
    defaultValues: {
      email: ''
    },
    resolver: yupResolver(validationSchema),
    mode: 'all'
  })

  async function onNotifyMe(values: FormValues) {
    try {
      setLoading(true)

      const { email } = values

      const emailsDoc = await getDoc(
        doc(db, 'email_alerts', 'inbound_subscribers')
      )
      const emails = get(emailsDoc.data(), 'emails', [])

      const location = await getLocation()

      const TriggerPage = 'Landing Page - Blogs'
      const pageName = window.location.href
      const emailBody = {
        template_name: 'new-signup',
        to_emails: emails,
        extra_data: {
          email,
          source: TriggerPage,
          url: pageName,
          location: location,
          date_time: moment()
            .tz('America/Los_Angeles')
            .format('MMMM Do YYYY, h:mm:ss a z'),
          redirection_url: localStorage.getItem('referrer') ?? 'Direct'
        }
      }

      await addDoc(collection(db, 'landing-page-visitors'), {
        ...emailBody,
        created_at: new Date().toISOString()
      })

      const country = await getCountry()
      if (country !== 'India') {
        await axios.post(
          `https://api.loopapplication.xyz/notification/email_digest`,
          emailBody
        )
      }

      posthog.capture('Landing Leads', {
        email,
        TriggerPage,
        pageName
      })

      let user = {
        signup_date: Date.now() / 1000,
        email
      }

      posthog.identify(user.email, user)

      setEmailSent(true)
      reset()

      setTimeout(() => {
        setEmailSent(false)
      }, 5000)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const getPosts = async () => {
      try {
        setBlogsLoading(true)
        let res = await getAllGhostPosts()
        if (res.posts) setBlogs(res.posts)
      } catch (err) {
        console.log(err)
      } finally {
        setBlogsLoading(false)
      }
    }

    getPosts()
  }, [])

  return (
    <div className="bg-white w-full flex flex-col items-center justify-center bg-white">
      <div className="bg-[#F2F2F2] w-full flex flex-col items-center justify-center">
        <div className="py-10 w-[90%]">
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              py: '30px'
            }}>
            <Grid item md={6}>
              <p className="mt-8 text-[30px] leading-8 md:text-[56px] md:leading-[63px] text-dark-blue text-left">
                Stay Updated with{' '}
                <span className="font-semibold">Loop's Latest News</span>
              </p>
            </Grid>
            <Grid item md={5}>
              <p className="lg:mt-0 mt-6">
                We have a lot of things happening around. <br />
                You want to stay in loop with us ?
              </p>
              <div className="flex align-flex-start mt-8">
                <CustomTextField
                  fullWidth
                  sx={{ background: 'white' }}
                  name="email"
                  type="email"
                  control={control}
                  label=""
                  placeholder="Enter your email"
                  errors={errors}
                />
                <button
                  className={`bg-[#196E82] text-white rounded-md ml-2 w-[150px] h-[55px] text-center text-xs font-semibold leading-8 ${
                    !isDirty || !isValid ? 'opacity-90' : 'cursor-pointer'
                  }`}
                  onClick={handleSubmit(onNotifyMe)}
                  disabled={!isDirty || !isValid}>
                  {loading ? (
                    <Image
                      src="/img/blogs/Loading.svg"
                      alt="Loading"
                      width={40}
                      height={40}
                      style={{ margin: '0 auto' }}
                    />
                  ) : emailSent ? (
                    <CheckCircle />
                  ) : (
                    <>
                      <ForwardToInboxIcon />
                      <span className="ml-2">Notify Me</span>
                    </>
                  )}
                </button>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>

      <div className="lg:w-[80%] w-[90%] py-10">
        {blogsLoading ? (
          <div className="flex justify-center">
            <Image
              src="/img/blogs/LoadingBlue.svg"
              alt="Loading"
              width={100}
              height={100}
            />
          </div>
        ) : (
          <>
            <div className="flex justify-center">
              <Tabs
                value={tabValue}
                onChange={(e, index) => setTabValue(index)}
                className="blog-tabs"
                TabIndicatorProps={{ style: { background: '#196E82' } }}>
                <Tab
                  label={
                    <div
                      className={`${
                        tabValue === 0 && 'font-semibold'
                      } text-[#696C80] flex items-end font-sora`}>
                      All
                    </div>
                  }
                  sx={{ width: '200px', borderBottom: '3px solid #BABDCC' }}
                />
                {blogs &&
                  blogs.filter((blog: any) => blog.external).length > 0 && (
                    <Tab
                      label={
                        <div
                          className={`${
                            tabValue === 1 && 'font-semibold'
                          } text-[#696C80] flex items-end font-sora`}>
                          <Image
                            src="img/blogs/PressReleaseIcon.svg"
                            alt="Press Release"
                            width={20}
                            height={20}
                          />{' '}
                          <span className="ml-2">Press Releases</span>
                        </div>
                      }
                      sx={{ width: '200px', borderBottom: '3px solid #BABDCC' }}
                    />
                  )}
                {blogs &&
                  blogs.filter((blog: any) => !blog.external && !blog.media)
                    .length > 0 && (
                    <Tab
                      label={
                        <div
                          className={`${
                            tabValue === 2 && 'font-semibold'
                          } text-[#696C80] flex items-end font-sora`}>
                          <Image
                            src="img/blogs/ArticlesIcon.svg"
                            alt="Articles"
                            width={20}
                            height={20}
                          />{' '}
                          <p className="ml-2">Articles</p>
                        </div>
                      }
                      sx={{ width: '200px', borderBottom: '3px solid #BABDCC' }}
                    />
                  )}

                {blogs &&
                  blogs.filter((blog: any) => !blog.media).length > 0 && (
                    <Tab
                      label={
                        <div
                          className={`${
                            tabValue === 2 && 'font-semibold'
                          } text-[#696C80] flex items-center font-sora`}>
                          <PermMedia
                            sx={{ color: '#196E82', fontSize: '24px' }}
                          />{' '}
                          <p className="ml-2">Media</p>
                        </div>
                      }
                      sx={{ width: '200px', borderBottom: '3px solid #BABDCC' }}
                    />
                  )}
              </Tabs>
            </div>

            <div className="my-6">
              <CustomTabPanel value={tabValue} index={0}>
                <BlogCardsSection loading={blogsLoading} blogs={blogs} />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={1}>
                <BlogCardsSection
                  loading={blogsLoading}
                  blogs={blogs && blogs.filter((blog: any) => blog.external)}
                />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={2}>
                <BlogCardsSection
                  loading={blogsLoading}
                  blogs={
                    blogs &&
                    blogs.filter((blog: any) => !blog.external && !blog.media)
                  }
                />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={3}>
                <BlogCardsSection
                  loading={blogsLoading}
                  blogs={blogs && blogs.filter((blog: any) => blog.media)}
                />
              </CustomTabPanel>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
